import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader, StatusChip } from "@/components/Bits";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import {
    Plus, ArrowsClockwise, CheckCircle, XCircle, Paperclip, Trash, Calendar, UsersThree,
} from "@phosphor-icons/react";

const LEAVE_TYPES = [
    { value: "casual", label: "Casual leave (max 2 consecutive; extras auto-LOP)" },
    { value: "medical", label: "Medical leave (needs certificate)" },
    { value: "lop", label: "Loss of pay (unpaid)" },
];

const currentYear = () => new Date().getFullYear();

export default function Leaves() {
    const { user } = useAuth();
    const isFounder = user?.role === "founder";
    const [tab, setTab] = useState("mine");
    const [year, setYear] = useState(currentYear());
    const [mine, setMine] = useState([]);
    const [pending, setPending] = useState([]);
    const [balances, setBalances] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [decisionTarget, setDecisionTarget] = useState(null); // {req, action}
    const [certTarget, setCertTarget] = useState(null);
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const promises = [
                api.get("/leaves/requests", { params: { year } }),
                api.get("/leaves/requests", { params: { pending_for_me: true } }),
                api.get("/employees").catch(() => ({ data: [] })),
            ];
            // Balance is FOUNDER-ONLY. Non-founders never fetch or render it.
            if (isFounder) {
                promises.push(api.get("/leaves/balances", { params: { year } }).catch(() => ({ data: [] })));
            }
            const [r, p, e, bal] = await Promise.all(promises);
            setMine(r.data);
            setPending(p.data);
            setEmployees(e.data);
            if (isFounder) setBalances(bal.data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [year, isFounder]);
    useEffect(() => { refresh(); }, [refresh]);

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Leaves"
                sub={isFounder
                    ? "12 Casual + 15 Medical per calendar year. Casual leaves max 2 consecutive; the 3rd+ day auto-flips to loss-of-pay. Medical leaves need a certificate before approval."
                    : "Apply for leave and track your requests. Casual leaves beyond 2 consecutive days auto-flip to loss-of-pay. Medical leaves need a certificate."}
                right={
                    <div className="flex items-center gap-2">
                        <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24" data-testid="lv-year" />
                        <Button variant="outline" onClick={refresh} disabled={loading} data-testid="lv-refresh">
                            <ArrowsClockwise size={14} className="mr-2" /> Refresh
                        </Button>
                        <Button onClick={() => setDialogOpen(true)} style={{ background: "#f59e0b", color: "#fff" }} data-testid="lv-new">
                            <Plus size={14} className="mr-2" /> Apply for leave
                        </Button>
                    </div>
                } />

            <div className="p-8 space-y-6">
                {/* Balance card — FOUNDER ONLY. Employees do not see how many
                    leaves they have left; if they want to know, they ask the
                    founder. */}

                <div className="flex items-center gap-2 border-b" style={{ borderColor: "var(--se-border)" }}>
                    <TabBtn tab="mine" cur={tab} set={setTab} label="Requests" count={mine.length} />
                    <TabBtn tab="pending" cur={tab} set={setTab} label="Approvals pending" count={pending.length} highlight={pending.length > 0} />
                    {isFounder ? <TabBtn tab="roster" cur={tab} set={setTab} label="Roster" count={balances.length} /> : null}
                </div>

                {tab === "mine" ? (
                    <RequestsTable rows={mine} onCancel={async (id) => {
                        if (!confirm("Cancel this request?")) return;
                        try { await api.post(`/leaves/requests/${id}/cancel`); toast.success("Cancelled"); refresh(); }
                        catch (e) { toast.error(formatApiError(e)); }
                    }} onAttachCert={(r) => setCertTarget(r)} testid="lv-mine-table" />
                ) : tab === "pending" ? (
                    <RequestsTable rows={pending}
                        onApprove={(r) => setDecisionTarget({ req: r, action: "approve" })}
                        onReject={(r) => setDecisionTarget({ req: r, action: "reject" })}
                        showApprover
                        testid="lv-pending-table" />
                ) : (
                    <RosterTable rows={balances} />
                )}
            </div>

            <NewRequestDialog open={dialogOpen} onOpenChange={setDialogOpen}
                employees={employees} isFounder={isFounder} onSaved={refresh} currentUser={user} />
            <DecisionDialog target={decisionTarget} onClose={() => setDecisionTarget(null)} onSaved={refresh} />
            <CertificateDialog target={certTarget} onClose={() => setCertTarget(null)} onSaved={refresh} />
        </>
    );
}

function TabBtn({ tab, cur, set, label, count, highlight }) {
    const active = cur === tab;
    return (
        <button className={`px-4 py-2 text-sm ${active ? "font-medium" : "text-stone-500"}`}
            style={active ? { borderBottom: "2px solid #f59e0b", color: "#f59e0b" } : {}}
            onClick={() => set(tab)} data-testid={`lv-tab-${tab}`}>
            {label} {count > 0 ? <span className={`ml-1 text-[10px] px-1 rounded ${highlight ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-600"}`}>{count}</span> : null}
        </button>
    );
}

function RequestsTable({ rows, onApprove, onReject, onCancel, onAttachCert, showApprover, testid }) {
    return (
        <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
            <table className="se-table" data-testid={testid}>
                <thead>
                    <tr>
                        <th>Employee</th><th>Type</th><th>Dates</th>
                        <th className="text-right">CL / ML / LOP</th>
                        <th>Reason</th><th>Cert</th><th>Status</th><th></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan="8" className="py-10 text-center text-stone-500">
                            No requests {showApprover ? "waiting for your approval" : "yet"}.
                        </td></tr>
                    ) : rows.map((r) => (
                        <tr key={r.id} data-testid={`lv-row-${r.id}`}>
                            <td>{r.employee_name} <span className="text-[10px] text-stone-500">({r.employee_code})</span></td>
                            <td className="text-xs capitalize">{r.leave_type}</td>
                            <td className="text-xs">{r.start_date} → {r.end_date}</td>
                            <td className="se-num text-right text-xs">
                                {r.casual_days || 0} / {r.medical_days || 0} / {r.lop_days || 0}
                            </td>
                            <td className="text-xs text-stone-600">{r.reason || "—"}</td>
                            <td className="text-xs">{r.medical_cert_document_id ? <Paperclip size={13} className="inline text-emerald-600" /> : "—"}</td>
                            <td><StatusChip status={r.status} /></td>
                            <td className="text-right">
                                {r.status === "pending" && onApprove ? (
                                    <>
                                        <Button size="sm" onClick={() => onApprove(r)}
                                            style={{ background: "#16a34a", color: "#fff" }}
                                            data-testid={`lv-approve-${r.id}`} className="mr-1">
                                            <CheckCircle size={13} />
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => onReject(r)}
                                            data-testid={`lv-reject-${r.id}`}>
                                            <XCircle size={13} />
                                        </Button>
                                    </>
                                ) : r.status === "pending" && onCancel ? (
                                    <>
                                        {r.leave_type === "medical" && !r.medical_cert_document_id && onAttachCert ? (
                                            <Button size="sm" variant="outline" onClick={() => onAttachCert(r)}
                                                data-testid={`lv-cert-${r.id}`} className="mr-1">
                                                <Paperclip size={13} className="mr-1" /> Cert
                                            </Button>
                                        ) : null}
                                        <Button size="sm" variant="outline" onClick={() => onCancel(r.id)}
                                            data-testid={`lv-cancel-${r.id}`}>
                                            <Trash size={13} />
                                        </Button>
                                    </>
                                ) : null}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function RosterTable({ rows }) {
    return (
        <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
            <div className="px-5 py-3 border-b font-serif text-lg flex items-center gap-2" style={{ borderColor: "var(--se-border)" }}>
                <UsersThree size={16} /> Leave balances — everyone
            </div>
            <table className="se-table" data-testid="lv-roster-table">
                <thead>
                    <tr>
                        <th>Employee</th><th>Department</th><th>Location</th>
                        <th className="text-right">Casual (avail / used)</th>
                        <th className="text-right">Medical (avail / used)</th>
                        <th className="text-right">LOP</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan="6" className="py-10 text-center text-stone-500">No employees.</td></tr>
                    ) : rows.map((r) => (
                        <tr key={r.employee_id} data-testid={`lv-roster-row-${r.employee_id}`}>
                            <td>{r.employee_name} <span className="text-[10px] text-stone-500">({r.employee_code})</span></td>
                            <td className="text-xs">{r.department || "—"}</td>
                            <td className="text-xs">{r.location || "—"}</td>
                            <td className="se-num text-right text-xs">
                                <span className="text-emerald-700 font-medium">{r.casual.available}</span>
                                <span className="text-stone-500"> / {r.casual.used}</span>
                            </td>
                            <td className="se-num text-right text-xs">
                                <span className="text-emerald-700 font-medium">{r.medical.available}</span>
                                <span className="text-stone-500"> / {r.medical.used}</span>
                            </td>
                            <td className="se-num text-right text-xs text-amber-700">{r.lop_days || 0}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function NewRequestDialog({ open, onOpenChange, employees, isFounder, onSaved, currentUser }) {
    const [empId, setEmpId] = useState("");
    const [type, setType] = useState("casual");
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) { setEmpId(""); setType("casual"); setStart(""); setEnd(""); setReason(""); }
    }, [open]);

    const submit = async () => {
        if (!start || !end) return toast.error("Pick start and end dates");
        setSaving(true);
        try {
            const body = { leave_type: type, start_date: start, end_date: end, reason };
            if (empId) body.employee_id = empId;
            await api.post("/leaves/requests", body);
            toast.success("Leave request submitted");
            onOpenChange(false); onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="lv-new-dialog">
                <DialogHeader><DialogTitle>Apply for leave</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    {isFounder || (currentUser?.role === "hr" || currentUser?.role === "accountant") ? (
                        <div><Label>Employee (optional — leave blank for self)</Label>
                            <select className="w-full border rounded-sm px-3 py-2 text-sm" value={empId}
                                onChange={(e) => setEmpId(e.target.value)} data-testid="lv-form-emp">
                                <option value="">— Self —</option>
                                {employees.map((e) => (
                                    <option key={e.id} value={e.id}>{e.name} ({e.employee_code})</option>
                                ))}
                            </select></div>
                    ) : null}
                    <div><Label>Leave type *</Label>
                        <select className="w-full border rounded-sm px-3 py-2 text-sm" value={type}
                            onChange={(e) => setType(e.target.value)} data-testid="lv-form-type">
                            {LEAVE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Start date *</Label>
                            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                                   min={type === "casual" ? (() => {
                                       const d = new Date(); d.setDate(d.getDate() + 2);
                                       return d.toISOString().slice(0, 10);
                                   })() : undefined}
                                   data-testid="lv-form-start" /></div>
                        <div><Label>End date *</Label>
                            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="lv-form-end" /></div>
                    </div>
                    <div><Label>Reason</Label>
                        <textarea className="w-full border rounded-sm px-3 py-2 text-sm min-h-16"
                            value={reason} onChange={(e) => setReason(e.target.value)} data-testid="lv-form-reason" /></div>
                    {/* Tier-aware and CL notice notes. Both are business rules
                        the founder wants surfaced up-front. */}
                    {(() => {
                        const selectedEmp = empId ? employees.find((e) => e.id === empId) : null;
                        // If a founder/HR/accountant has picked a specific employee, use
                        // their tier. Otherwise (self-serve) we don't have the current
                        // user's employee record on the client, so surface a generic
                        // hint for casual/medical.
                        const isLabour = selectedEmp?.tier === "labour";
                        return (
                            <>
                                {isLabour && (type === "casual" || type === "medical") ? (
                                    <div className="text-xs text-amber-900 border rounded-md p-3"
                                         style={{ borderColor: "#fde68a", background: "#fffbeb" }}
                                         data-testid="lv-note-labour">
                                        <Calendar size={13} className="inline mr-1" />
                                        <b>Labour employees don&apos;t get Casual or Medical leave.</b>{" "}
                                        Any absence is Loss-of-Pay — this request will be recorded as LOP
                                        (payroll deducts these days).
                                    </div>
                                ) : null}
                                {type === "medical" ? (
                                    <div className="text-xs text-stone-600 border rounded-md p-3" style={{ borderColor: "var(--se-border)", background: "#eff6ff" }}>
                                        <Paperclip size={13} className="inline mr-1" />
                                        After submitting, upload the medical certificate on the request. Without a cert, the manager can only reject (which flips to LOP).
                                    </div>
                                ) : type === "casual" ? (
                                    <div className="text-xs text-stone-600 border rounded-md p-3" style={{ borderColor: "var(--se-border)", background: "#fefce8" }}>
                                        <Calendar size={13} className="inline mr-1" />
                                        <b>Notice period:</b> Casual leave needs at least 1 clear day of notice.
                                        E.g. for a leave on the 6th, apply on or before the 4th.
                                        Same-day / next-day emergencies must be raised by the founder.
                                        <br />
                                        <b>Consecutive rule:</b> Max 2 consecutive CL days — any working day beyond that automatically becomes LOP for this request.
                                    </div>
                                ) : null}
                            </>
                        );
                    })()}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: "#f59e0b", color: "#fff" }} data-testid="lv-form-save">
                        {saving ? "Submitting..." : "Submit"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DecisionDialog({ target, onClose, onSaved }) {
    const [note, setNote] = useState("");
    const [flipToLop, setFlipToLop] = useState(false);
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (target) { setNote(""); setFlipToLop(target.req.leave_type === "medical" && !target.req.medical_cert_document_id); } }, [target]);
    if (!target) return null;
    const { req, action } = target;

    const submit = async () => {
        setBusy(true);
        try {
            if (action === "approve") {
                await api.post(`/leaves/requests/${req.id}/approve`, { note });
                toast.success("Approved");
            } else {
                await api.post(`/leaves/requests/${req.id}/reject`, { reason: note, flip_to_lop: flipToLop });
                toast.success(flipToLop ? "Rejected → LOP" : "Rejected");
            }
            onSaved && onSaved(); onClose();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg" data-testid="lv-decision-dialog">
                <DialogHeader><DialogTitle>
                    {action === "approve" ? "Approve" : "Reject"} — {req.employee_name}
                </DialogTitle></DialogHeader>
                <div className="space-y-3 text-sm">
                    <div className="text-xs text-stone-600 border rounded-md p-3" style={{ borderColor: "var(--se-border)", background: "#f8fafc" }}>
                        <div><strong>Type:</strong> {req.leave_type} · <strong>Dates:</strong> {req.start_date} → {req.end_date}</div>
                        <div><strong>Days:</strong> {req.casual_days}CL / {req.medical_days}ML / {req.lop_days}LOP</div>
                        {req.reason ? <div><strong>Reason:</strong> {req.reason}</div> : null}
                        {req.leave_type === "medical" && !req.medical_cert_document_id ? (
                            <div className="text-red-700 mt-2"><Paperclip size={13} className="inline mr-1" />No medical certificate attached — approval blocked. Reject or ask the employee to attach the cert.</div>
                        ) : null}
                    </div>
                    <div><Label>{action === "approve" ? "Approval note" : "Reason"}</Label>
                        <textarea className="w-full border rounded-sm px-3 py-2 text-sm min-h-16"
                            value={note} onChange={(e) => setNote(e.target.value)} data-testid="lv-decision-note" /></div>
                    {action === "reject" ? (
                        <label className="flex items-start gap-2 text-xs text-stone-700 cursor-pointer">
                            <input type="checkbox" checked={flipToLop} onChange={(e) => setFlipToLop(e.target.checked)} data-testid="lv-decision-lop" className="mt-1" />
                            <span>Convert to Loss-of-Pay (payroll still deducts these days). Recommended for medical without cert.</span>
                        </label>
                    ) : null}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy}
                        style={{ background: action === "approve" ? "#16a34a" : "#dc2626", color: "#fff" }}
                        data-testid="lv-decision-submit">
                        {busy ? "Saving..." : action === "approve" ? "Approve" : "Reject"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CertificateDialog({ target, onClose, onSaved }) {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (target) setFile(null); }, [target]);
    if (!target) return null;

    const upload = async () => {
        if (!file) return toast.error("Pick a file");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/leaves/requests/${target.id}/certificate`, fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Certificate attached");
            onSaved && onSaved(); onClose();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="lv-cert-dialog">
                <DialogHeader><DialogTitle>Upload medical certificate</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="text-xs text-stone-500">
                        For medical leave <strong>{target.start_date} → {target.end_date}</strong>. Manager will need this before approving.
                    </div>
                    <Input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="lv-cert-file" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={upload} disabled={busy || !file} style={{ background: "#f59e0b", color: "#fff" }} data-testid="lv-cert-upload">
                        {busy ? "Uploading..." : "Upload"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
